<template>
  <div class="container-fluid">
    <div class="row">
      <div class="col-12">
        <div class="card shadow-sm">
          <div class="card-header bg-primary text-white">
            <h5 class="mb-0">
              <i class="bi bi-bell-fill me-2"></i>
              Envoyer une notification aux utilisateurs PDA
            </h5>
          </div>
          <div class="card-body">
            <form @submit.prevent="sendNotification">
              <!-- Message -->
              <div class="mb-3">
                <label for="message" class="form-label fw-semibold">
                  Message <span class="text-danger">*</span>
                </label>
                <textarea
                  id="message"
                  v-model="form.message"
                  class="form-control"
                  rows="4"
                  placeholder="Entrez votre message ici..."
                  required
                ></textarea>
                <small class="text-muted">
                  {{ form.message.length }}/500 caractères
                </small>
              </div>

              <!-- Notification Type -->
              <div class="mb-3">
                <label for="type" class="form-label fw-semibold">
                  Type de notification
                </label>
                <select id="type" v-model="form.type" class="form-select">
                  <option value="info">
                    <i class="bi bi-info-circle"></i> Information
                  </option>
                  <option value="success">
                    <i class="bi bi-check-circle"></i> Succès
                  </option>
                  <option value="warning">
                    <i class="bi bi-exclamation-triangle"></i> Avertissement
                  </option>
                  <option value="error">
                    <i class="bi bi-x-circle"></i> Erreur
                  </option>
                </select>
              </div>

              <!-- Target Selection -->
              <div class="mb-3">
                <label class="form-label fw-semibold">Destinataire</label>
                <div class="form-check">
                  <input
                    class="form-check-input"
                    type="radio"
                    name="target"
                    id="targetAll"
                    value="all"
                    v-model="form.target"
                  />
                  <label class="form-check-label" for="targetAll">
                    <i class="bi bi-people-fill me-1"></i>
                    Tous les utilisateurs PDA
                  </label>
                </div>
                <div class="form-check">
                  <input
                    class="form-check-input"
                    type="radio"
                    name="target"
                    id="targetUser"
                    value="user"
                    v-model="form.target"
                  />
                  <label class="form-check-label" for="targetUser">
                    <i class="bi bi-person-fill me-1"></i>
                    Utilisateur spécifique
                  </label>
                </div>
              </div>

              <!-- User Selection (if target is user) -->
              <div v-if="form.target === 'user'" class="mb-3">
                <label for="userId" class="form-label fw-semibold">
                  Sélectionner un utilisateur
                </label>
                <select
                  id="userId"
                  v-model="form.userId"
                  class="form-select"
                  :required="form.target === 'user'"
                >
                  <option value="">-- Sélectionner un utilisateur --</option>
                  <option
                    v-for="user in users"
                    :key="user.reference || user.id"
                    :value="user.reference || user.id"
                  >
                    {{ user.name }} ({{ user.username || user.reference }})
                  </option>
                </select>
              </div>

              <!-- Additional Data (Optional) -->
              <div class="mb-3">
                <label class="form-label fw-semibold">
                  Données supplémentaires (optionnel)
                </label>
                <div class="input-group mb-2">
                  <span class="input-group-text">Clé</span>
                  <input
                    type="text"
                    v-model="dataKey"
                    class="form-control"
                    placeholder="ex: order_id"
                  />
                  <span class="input-group-text">Valeur</span>
                  <input
                    type="text"
                    v-model="dataValue"
                    class="form-control"
                    placeholder="ex: 123"
                  />
                  <button
                    type="button"
                    class="btn btn-outline-secondary"
                    @click="addData"
                  >
                    <i class="bi bi-plus"></i> Ajouter
                  </button>
                </div>
                <div v-if="Object.keys(form.data).length > 0" class="mt-2">
                  <small class="text-muted d-block mb-1"
                    >Données ajoutées:</small
                  >
                  <div
                    v-for="(value, key) in form.data"
                    :key="key"
                    class="badge bg-secondary me-1 mb-1"
                  >
                    {{ key }}: {{ value }}
                    <button
                      type="button"
                      class="btn-close btn-close-white ms-1"
                      style="font-size: 0.7em"
                      @click="removeData(key)"
                    ></button>
                  </div>
                </div>
              </div>

              <!-- Preview -->
              <div class="mb-3">
                <label class="form-label fw-semibold">Aperçu</label>
                <div
                  class="alert"
                  :class="{
                    'alert-info': form.type === 'info',
                    'alert-success': form.type === 'success',
                    'alert-warning': form.type === 'warning',
                    'alert-danger': form.type === 'error',
                  }"
                >
                  <strong>{{ getTypeLabel(form.type) }}:</strong>
                  {{ form.message || "Votre message apparaîtra ici..." }}
                </div>
              </div>

              <!-- Action Buttons -->
              <div class="d-flex justify-content-between">
                <button
                  type="button"
                  class="btn btn-secondary"
                  @click="resetForm"
                >
                  <i class="bi bi-arrow-counterclockwise me-1"></i>
                  Réinitialiser
                </button>
                <button
                  type="submit"
                  class="btn btn-primary"
                  :disabled="loading || !form.message"
                >
                  <span
                    v-if="loading"
                    class="spinner-border spinner-border-sm me-1"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  <i v-else class="bi bi-send-fill me-1"></i>
                  {{
                    loading ? "Envoi en cours..." : "Envoyer la notification"
                  }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "SendNotification",
  data() {
    return {
      form: {
        message: "",
        type: "info",
        target: "all",
        userId: null,
        data: {},
      },
      dataKey: "",
      dataValue: "",
      users: [],
      loading: false,
    };
  },
  mounted() {
    this.loadUsers();
  },
  methods: {
    async loadUsers() {
      try {
        const response = await this.$api.show("users");
        this.users = response.data || response || [];
      } catch (error) {
        console.error("Error loading users:", error);
        this.$toast.error("Erreur lors du chargement des utilisateurs");
      }
    },
    addData() {
      if (this.dataKey && this.dataValue) {
        this.$set(this.form.data, this.dataKey, this.dataValue);
        this.dataKey = "";
        this.dataValue = "";
      }
    },
    removeData(key) {
      this.$delete(this.form.data, key);
    },
    getTypeLabel(type) {
      const labels = {
        info: "Information",
        success: "Succès",
        warning: "Avertissement",
        error: "Erreur",
      };
      return labels[type] || "Information";
    },
    async sendNotification() {
      if (!this.form.message) {
        this.$toast.error("Veuillez entrer un message");
        return;
      }

      if (this.form.target === "user" && !this.form.userId) {
        this.$toast.error("Veuillez sélectionner un utilisateur");
        return;
      }

      this.loading = true;

      try {
        let endpoint = "notifications/broadcast/all";
        let payload = {
          message: this.form.message,
          type: this.form.type,
          data: this.form.data,
        };

        if (this.form.target === "user") {
          endpoint = `notifications/broadcast/user/${this.form.userId}`;
        }

        const response = await this.$api.create(endpoint, payload);

        if (response.success) {
          this.$toast.success(
            `Notification envoyée avec succès${
              this.form.target === "user"
                ? " à l'utilisateur"
                : " à tous les utilisateurs"
            }!`
          );
          this.resetForm();
        } else {
          throw new Error(response.message || "Erreur lors de l'envoi");
        }
      } catch (error) {
        console.error("Error sending notification:", error);
        const errorMessage =
          (error.response &&
            error.response.data &&
            error.response.data.message) ||
          error.message ||
          "Erreur lors de l'envoi de la notification";
        this.$toast.error(errorMessage);
      } finally {
        this.loading = false;
      }
    },
    resetForm() {
      this.form = {
        message: "",
        type: "info",
        target: "all",
        userId: null,
        data: {},
      };
      this.dataKey = "";
      this.dataValue = "";
    },
  },
};
</script>

<style scoped>
.card {
  border: none;
  border-radius: 10px;
}

.card-header {
  border-radius: 10px 10px 0 0 !important;
}

.form-label {
  color: #333;
}

.badge {
  padding: 0.5em 0.75em;
  font-size: 0.875em;
}

.alert {
  border-radius: 8px;
}

textarea {
  resize: vertical;
  min-height: 100px;
}
</style>
